import { useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FileDownloadIcon,
  Radio,
  RadioGroup,
  Spinner,
  Text,
} from '@billda/design-system';
import { useMutation } from '@tanstack/react-query';
import {
  downloadActivityCsv,
  type ActivityIncludeMode,
} from '../api/activityApi';
import { useActivitySites } from '../hooks/useActivitySites';
import { SiteMultiSelect } from './ActivityFilterContent';

interface ExportActivityModalProps {
  open: boolean;
  onClose: () => void;
  from: string;
  to: string;
  siteIds: number[];
  type: '' | 'Labour' | 'Variation';
}

export function ExportActivityModal({
  open,
  onClose,
  from,
  to,
  siteIds,
  type,
}: ExportActivityModalProps) {
  const { data: sites = [], isLoading: sitesLoading } = useActivitySites();
  const [exportFrom, setExportFrom] = useState<string | null>(from);
  const [exportTo, setExportTo] = useState<string | null>(to);
  const [exportSiteIds, setExportSiteIds] = useState<number[]>(siteIds);
  const [mode, setMode] = useState<ActivityIncludeMode>('booked-approved');

  // Seed the modal from the current page filters each time it opens, so it
  // reflects the latest date window and site selection rather than stale state.
  useEffect(() => {
    if (open) {
      setExportFrom(from);
      setExportTo(to);
      setExportSiteIds(siteIds);
    }
  }, [open, from, to, siteIds]);

  const typeSummary =
    type === ''
      ? 'Labour + Variations'
      : type === 'Labour'
        ? 'Labour only'
        : 'Variations only';

  const exportMutation = useMutation({
    mutationFn: downloadActivityCsv,
    onSuccess: onClose,
  });

  const canExport =
    Boolean(exportFrom) && Boolean(exportTo) && !exportMutation.isPending;

  const handleExport = () => {
    if (!exportFrom || !exportTo) {
      return;
    }
    exportMutation.mutate({
      from: exportFrom,
      to: exportTo,
      siteIds: exportSiteIds,
      type,
      include: mode,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent data-testid="activity-export-modal" className="max-w-xs">
        <DialogHeader>Export activity to CSV</DialogHeader>
        <DialogBody>
          <div className="mt-1 flex flex-col gap-6">
            <div>
              <Text variant="body2" className="mb-2 font-semibold">
                Date range
              </Text>
              <div className="flex flex-col gap-2">
                <DatePicker
                  label="From"
                  value={exportFrom ? new Date(exportFrom) : null}
                  onChange={d => {
                    setExportFrom(d ? d.toISOString() : null);
                  }}
                />
                <DatePicker
                  label="To"
                  value={exportTo ? new Date(exportTo) : null}
                  onChange={d => {
                    setExportTo(d ? d.toISOString() : null);
                  }}
                />
              </div>
            </div>

            <div>
              <Text variant="body2" className="mb-2 font-semibold">
                Sites
              </Text>
              <SiteMultiSelect
                sites={sites}
                selectedIds={exportSiteIds}
                onChange={setExportSiteIds}
                disabled={sites.length === 0}
                loadingLabel={
                  sitesLoading
                    ? 'Loading sites…'
                    : sites.length === 0
                      ? 'No sites available'
                      : 'All sites'
                }
                testId="activity-export-site-select"
              />
            </div>

            <div>
              <Text variant="body2" className="mb-2 font-semibold">
                Include
              </Text>
              <RadioGroup
                value={mode}
                onValueChange={value => {
                  setMode(value as ActivityIncludeMode);
                }}
              >
                <label className="flex cursor-pointer items-center gap-2 type-body-md text-content-primary">
                  <Radio value="booked-approved" />
                  Booked &amp; approved
                </label>
                <label className="flex cursor-pointer items-center gap-2 type-body-md text-content-primary">
                  <Radio value="approved" />
                  Approved only
                </label>
              </RadioGroup>
            </div>

            <div>
              <Text variant="body2" color="secondary">
                Work type: {typeSummary}
              </Text>
              <Text variant="caption" color="secondary">
                Rejected and cancelled work is never exported.
              </Text>
            </div>
          </div>
        </DialogBody>
        <DialogFooter className="px-6 pb-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="neutral"
            onClick={handleExport}
            disabled={!canExport}
            data-testid="activity-export-confirm"
          >
            {exportMutation.isPending ? (
              <Spinner size={20} color="inherit" />
            ) : (
              <FileDownloadIcon aria-hidden="true" className="size-5" />
            )}
            {exportMutation.isPending ? 'Exporting…' : 'Export CSV'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
